import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Users, ArrowRight, Shield, User as UserIcon, Sparkles } from "lucide-react";

const ROLE_PRETTY = {
  agent: "Agent",
  agency_admin: "Agency Admin",
  admin: "Admin",
  support: "Support",
};
const AGENT_TYPE_PRETTY = { employee: "Employee", subagent: "Subagent" };

export default function ReassignDealerDialog({ open, onboardingId, currentRecord, onOpenChange, onReassigned }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [reason, setReason] = useState("");
  const [filter, setFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !onboardingId) return;
    setLoading(true);
    setReason("");
    setFilter("");
    setSelectedId("");
    api.get(`/onboarding/${onboardingId}/reassign-candidates`)
      .then(({ data }) => setCandidates(data || []))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Unable to load candidates");
        setCandidates([]);
      })
      .finally(() => setLoading(false));
  }, [open, onboardingId]);

  // Recommended pick: agency_admin of the current owner's agency.
  const recommended = useMemo(() => {
    if (!currentRecord?.agency) return null;
    return candidates.find(
      (c) => c.role === "agency_admin" &&
        (c.agency || "").toLowerCase() === (currentRecord.agency || "").toLowerCase(),
    ) || null;
  }, [candidates, currentRecord?.agency]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (c) => (c.name || "").toLowerCase().includes(q) ||
             (c.email || "").toLowerCase().includes(q) ||
             (c.agency || "").toLowerCase().includes(q),
    );
  }, [candidates, filter]);

  const submit = async () => {
    if (!selectedId) {
      toast.error("Pick a user to reassign to");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/onboarding/${onboardingId}/reassign-owner`, {
        new_owner_id: selectedId, reason: reason || null,
      });
      toast.success("Dealer reassigned");
      onReassigned?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reassign failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="reassign-dealer-dialog">
        <DialogHeader className="space-y-1">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Reassign dealer</div>
          <DialogTitle className="font-display text-2xl font-black text-[#0B132B]">
            {currentRecord?.dealer_name || "Reassign owner"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6C757D]">
            Currently owned by <b>{currentRecord?.signing_agent_name || "—"}</b>
            {currentRecord?.agency ? <> at <b>{currentRecord.agency}</b></> : null}. Pick a new owner below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {recommended && (
            <button
              type="button"
              onClick={() => setSelectedId(recommended.id)}
              className={`w-full text-left flex items-center gap-3 p-3 rounded-sm border-2 transition-colors ${
                selectedId === recommended.id
                  ? "border-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-[#D4AF37]/40 bg-[#D4AF37]/5 hover:bg-[#D4AF37]/10"
              }`}
              data-testid="reassign-recommended-row"
            >
              <Sparkles size={14} className="text-[#D4AF37] shrink-0" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF37]">Recommended</div>
                <div className="text-sm font-semibold text-[#0B132B]">
                  {recommended.name} — Agency Admin
                </div>
                <div className="text-[11px] text-[#6C757D]">{recommended.email}</div>
              </div>
            </button>
          )}

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">Search users</Label>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Name, email, or agency…"
              className="mt-2 h-10 rounded-sm border-[#0B132B]/15"
              data-testid="reassign-search-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">New owner</Label>
            <Select value={selectedId} onValueChange={setSelectedId} disabled={loading}>
              <SelectTrigger
                className="mt-2 h-10 rounded-sm border-[#0B132B]/15"
                data-testid="reassign-user-select-trigger"
              >
                <SelectValue placeholder={loading ? "Loading…" : `${filtered.length} user${filtered.length === 1 ? "" : "s"} available`} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {filtered.length === 0 && (
                  <div className="text-xs text-[#6C757D] px-3 py-2">No users match</div>
                )}
                {filtered.map((c) => (
                  <SelectItem
                    key={c.id}
                    value={c.id}
                    data-testid={`reassign-user-option-${c.id}`}
                  >
                    <div className="flex items-center gap-2">
                      {c.role === "admin" || c.role === "agency_admin" ? (
                        <Shield size={12} className="text-[#D4AF37]" />
                      ) : (
                        <UserIcon size={12} className="text-[#6C757D]" />
                      )}
                      <span className="font-semibold">{c.name || c.email}</span>
                      <span className="text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 bg-[#0B132B]/5 text-[#0B132B] font-bold rounded-sm">
                        {ROLE_PRETTY[c.role] || c.role}
                      </span>
                      {c.role === "agent" && c.agent_type && (
                        <span className="text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 bg-[#D4AF37]/10 text-[#0B132B] font-bold rounded-sm">
                          {AGENT_TYPE_PRETTY[c.agent_type] || c.agent_type}
                        </span>
                      )}
                      {c.agency && <span className="text-[11px] text-[#6C757D]">· {c.agency}</span>}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
              Reason <span className="text-[10px] font-normal text-[#6C757D] normal-case tracking-normal">(optional)</span>
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Why is this being reassigned?"
              className="mt-2 rounded-sm border-[#0B132B]/15"
              data-testid="reassign-reason-input"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
            data-testid="reassign-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !selectedId}
            className="rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white"
            data-testid="reassign-confirm"
          >
            <Users size={14} className="mr-1.5" />
            {submitting ? "Reassigning…" : "Reassign dealer"}
            <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
